// Un filtro donde se pueden elegir VARIOS valores a la vez.
//
// Lissette 2026-08-28: "en el filtro por aseguradora tanto en cliente como en
// pólizas tener la opción de elegir varias a la vez".
//
// Por qué un componente y no un `<select multiple>`: el select múltiple del
// navegador obliga a mantener Ctrl pulsado para sumar opciones — quien no lo
// sepa pierde su selección anterior en cada clic y no hay nada que se lo diga.
// Aquí son casillas: se pulsa y se suma.
//
// Vive aquí, en un solo sitio, porque lo usan Clientes y Pólizas. Dos copias se
// separan en cuanto alguien toque una (E-138).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { alternarSeleccion, etiquetaDeSeleccion } from '../utils.js';

/**
 * @param {string[]} opciones      Los valores que hay de verdad a la vista.
 * @param {string[]} seleccion     Lo elegido. Vacío = todas.
 * @param {(l:string[])=>void} onChange
 * @param {(v:string)=>number} [contar]  Cuántas filas hay por valor.
 * @param {(v:string)=>string} [etiquetaDe]  Cómo se LEE cada valor.
 *
 * `etiquetaDe` existe para los catálogos donde el valor guardado no es el
 * texto: el estatus migratorio se guarda `permanent_resident` y se lee
 * "Residente Permanente". Sin esto, la lista enseñaría los códigos en crudo y
 * el buscador de dentro no encontraría "residente".
 */
export function FiltroMultiple({ opciones, seleccion, onChange, contar, etiquetaDe, icono = '🏢', textoTodas, titulo, lang = 'es' }) {
  const es = lang === 'es';
  const [abierto, setAbierto] = useState(false);
  const [busca, setBusca] = useState('');
  const caja = useRef(null);

  // Cerrar al pulsar fuera y con Escape. Sin esto el panel se queda abierto
  // tapando la tabla que se acaba de filtrar, que es justo lo que se quiere ver.
  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => { if (caja.current && !caja.current.contains(e.target)) setAbierto(false); };
    const tecla = (e) => { if (e.key === 'Escape') setAbierto(false); };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', tecla);
    return () => { document.removeEventListener('mousedown', fuera); document.removeEventListener('keydown', tecla); };
  }, [abierto]);

  const elegidas = Array.isArray(seleccion) ? seleccion : [];
  const leer = (v) => (etiquetaDe ? etiquetaDe(v) : v);

  // Un valor guardado que ya no existe a la vista seguiría filtrando sin
  // aparecer en la lista: la pantalla saldría vacía y nada explicaría por qué.
  // Se limpia solo, igual que hacen los filtros de un solo valor.
  useEffect(() => {
    const vivas = elegidas.filter((v) => opciones.includes(v));
    if (vivas.length !== elegidas.length) onChange(vivas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opciones]);

  const visibles = useMemo(() => {
    const q = busca.trim().toLowerCase();
    // Se busca por lo que se LEE, no por el código guardado: nadie escribe
    // "permanent_resident" en la cajita, escribe "residente".
    return q ? opciones.filter((o) => String(leer(o)).toLowerCase().includes(q)) : opciones;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opciones, busca, etiquetaDe]);

  const texto = etiquetaDeSeleccion(
    // Con UNA sola elegida el botón enseña ESA. Pasando los códigos en crudo
    // pondría "permanent_resident" en la barra de filtros.
    elegidas.map(leer),
    textoTodas || (es ? `Toda Aseguradora (${opciones.length})` : `All Carriers (${opciones.length})`),
    es ? '{n} seleccionadas' : '{n} selected',
  );

  return (
    <div ref={caja} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="dkt-inp"
        title={titulo || (es ? 'Filtrar por aseguradora' : 'Filter by carrier')}
        style={{
          fontSize: '.73rem', padding: '.4rem .75rem', width: 'auto', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'inherit',
          fontWeight: elegidas.length ? 700 : 500,
        }}>
        <span>{icono}</span>
        <span style={{ maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{texto}</span>
        <span style={{ opacity: .6 }}>▾</span>
      </button>

      {elegidas.length > 0 && (
        <button type="button" onClick={() => onChange([])}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '.35rem .6rem', borderRadius: 7, cursor: 'pointer', border: '1px solid var(--c-border,#1a3550)', background: 'var(--c-surface2,#112538)', color: 'var(--c-text2,#7aa3c0)', fontSize: '.7rem', fontWeight: 600, fontFamily: 'inherit' }}>
          <X size={11} />{es ? 'Limpiar' : 'Clear'}
        </button>
      )}

      {abierto && (
        <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6, zIndex: 60, minWidth: 260, maxWidth: 320, background: 'var(--c-surface,#0d1f31)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,.35)', padding: 8 }}>
          {/* El buscador solo aparece cuando de verdad hace falta: con seis
              aseguradoras estorba más de lo que ayuda. */}
          {opciones.length > 8 && (
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder={es ? 'Buscar…' : 'Search…'}
              className="dkt-inp"
              style={{ width: '100%', fontSize: '.75rem', padding: '.35rem .6rem', marginBottom: 6, boxSizing: 'border-box' }}
              autoFocus />
          )}

          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <button type="button" onClick={() => onChange([...opciones])} className="dkt-btn-s" style={{ flex: 1, fontSize: '.7rem', padding: '.3rem' }}>
              {es ? 'Todas' : 'All'}
            </button>
            <button type="button" onClick={() => onChange([])} className="dkt-btn-s" style={{ flex: 1, fontSize: '.7rem', padding: '.3rem' }}>
              {es ? 'Ninguna' : 'None'}
            </button>
          </div>

          <div style={{ maxHeight: 260, overflowY: 'auto' }}>
            {visibles.length === 0
              ? <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', padding: '8px 4px' }}>{es ? 'Sin coincidencias.' : 'No matches.'}</div>
              : visibles.map((op) => {
                const on = elegidas.includes(op);
                const n = contar ? contar(op) : null;
                return (
                  <label key={op}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px', borderRadius: 6, cursor: 'pointer', fontSize: '.76rem', color: 'var(--c-text,#c8dff0)', background: on ? 'rgba(59,130,246,.14)' : 'transparent' }}>
                    <input type="checkbox" checked={on} onChange={() => onChange(alternarSeleccion(elegidas, op))} style={{ cursor: 'pointer' }} />
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{leer(op)}</span>
                    {n != null && <span style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)' }}>({n})</span>}
                  </label>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}
